import { ReactElement } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Accordion, LinkButton } from "@/DesignSystem";
import { CareElementConfig } from "@/Flowsheets/Models/CareElements/CareElementConfig";
import { defaultConfiguration } from "@/Flowsheets/Models/CareElements/DefaultCareComponentConfig";
import { FlowsheetConfig } from "@/Flowsheets/Models/CareElements/FlowsheetConfig";
import { CareComponentConfiguration } from "./CareComponentConfiguration";

import configurationStepStyles from "./ConfigurationStep.module.scss";
import styles from "./ConfigureFlowsheetModal.module.scss";

/**
 * The properties for the {@link ConfigurationStep} component.
 * This interface defines the configuration data and callback functions required for managing care components in a flowsheet.
 */
interface IConfigurationStepProps {
    /**
     * The configuration object for the care components in the flowsheet.
     * This contains the settings and structure for the flowsheet's care components.
     */
    careComponentsConfig: FlowsheetConfig;

    /**
     * Callback function triggered when the link button for a care component is clicked.
     * This is used to activate or deactivate a care component.
     *
     * @param componentName - The name of the care component being linked or unlinked.
     * @param isActivated - A boolean indicating whether the care component is being activated (true) or deactivated (false).
     */
    onComponentLinkButtonClick: ({ careComponentName, isActivated }: { careComponentName: string, isActivated: boolean }) => void;

    onSectionOpenStates: ({ careComponentName, isOpen }: { careComponentName: string, isOpen: boolean }) => void;

    /**
     * Callback function triggered when a care element is removed from a care component.
     * This is used to handle the removal of specific elements from a care component.
     *
     * @param componentName - The name of the care component from which the care element is being removed.
     * @param careElementName - The name of the care element being removed.
     */
    onRemoveCareElement: ({ careComponentName, careElementName }: { careComponentName: string, careElementName: string }) => void;

    /**
     * Callback function triggered when a care element is added to a care component.
     * This is used to handle the addition of specific elements to a care component.
     *
     * @param componentName - The name of the care component to which the care element is being added.
     * @param careElementName - The name of the care element being added.
     */
    onAddCareElement: ({ careComponentName, careElementName }: { careComponentName: string, careElementName: string }) => void;

    /**
     * Callback function triggered when a care element configuration is edited.
     * @param careComponentName - The name of the care component containing the care element.
     * @param careElementName - The name of the care element being edited.
     * @param careElementConfig - The new configuration for the care element.
     */
    editCareElementConfig: ({ careComponentName, careElementName, careElementConfig }: { careComponentName: string, careElementName: string, careElementConfig: CareElementConfig, onValidate?: (invalidConfiguration: boolean) => void }) => void;

    /**
     * A set of strings representing invalid care elements.
     */
    invalidCareElements: ReadonlySet<string>;
}

function ConfigurationStep(props: Readonly<IConfigurationStepProps>): ReactElement {
    const { getText } = useTranslation();

    return (
        <>
            {props.careComponentsConfig && defaultConfiguration.CareComponents.map(careComponent => {
                const currentComponent = props.careComponentsConfig.CareComponents.find(
                    c => c.Name === careComponent.Name
                );

                const defaultSubSections = careComponent.CareElementsConfig.toLookup(p => p.SubSectionName);
                const defaultSubSectionNames = Object.keys(defaultSubSections);
                const currentSubSections = currentComponent?.CareElementsConfig.toLookup(p => p.SubSectionName ?? "");
                return (
                    <Accordion
                        key={`${careComponent.Name}_Accordion`}
                        title={getText(`Flowsheets_${careComponent.Name}_Title`)}
                        isOpen={currentComponent.Expanded ?? false}
                        titleSize="title-md"
                        variant="tertiary"
                        onToggle={currentComponent.IsActive ?
                            isOpen => { props.onSectionOpenStates({ careComponentName: careComponent.Name, isOpen: isOpen }); }
                            : () => {}
                        }
                        actionButtons={[
                            <LinkButton
                                dataCy={`flowsheets-configuration-${careComponent.Name}-show-hide-button`}
                                key={`${careComponent.Name}_linkButton`}
                                label={currentComponent.IsActive ? getText("Flowsheets_HideLabel") : getText("Flowsheets_ShowLabel")}
                                underline={false}
                                onClick={e => {
                                    e.stopPropagation();
                                    props.onComponentLinkButtonClick({ careComponentName: careComponent.Name, isActivated: !currentComponent.IsActive });
                                }}
                            />
                        ]}
                        isPassive={!currentComponent.IsActive}
                    >
                        {currentComponent && (
                            defaultSubSectionNames.map(sectionName => (
                                <div key={`${careComponent.Name}_${sectionName}`}>
                                    {sectionName && <p className={configurationStepStyles.subSectionTitle}>{getText(`Flowsheets_${careComponent.Name}_SubSectionName_${sectionName}`)}</p>}
                                    <div className={styles.careComponentConfigurationContainer}>
                                        <CareComponentConfiguration
                                            careComponentName={careComponent.Name}
                                            careElements={currentSubSections[sectionName] ?? []}
                                            availableElements={defaultSubSections[sectionName].filter(defaultElement =>
                                                !currentSubSections[sectionName]?.some(activeElement =>
                                                    activeElement.Name === defaultElement.Name
                                                ))}
                                            onRemove={careElementName => props.onRemoveCareElement({ careComponentName: careComponent.Name, careElementName })}
                                            onAdd={careElementName => props.onAddCareElement({ careComponentName: careComponent.Name, careElementName: careElementName })}
                                            onEdit={(careElementName, careElementConfig) =>
                                                props.editCareElementConfig({
                                                    careComponentName: careComponent.Name,
                                                    careElementName,
                                                    careElementConfig
                                                })
                                            }
                                            isCareElementInvalid={(careElementName: string) =>
                                                props.invalidCareElements.has(`${careComponent.Name}::${careElementName}`)
                                            }
                                        />
                                    </div>
                                </div>
                            ))

                        )}
                    </Accordion>
                );
            })}
        </>
    );
}

export { ConfigurationStep };
